import { useState } from 'react';
import { Info, ArrowLeft, ArrowRight } from 'lucide-react';
import './styles/PlatformConfiguration.css';

interface ExecutiveDashboardConfigProps {
  nextStep: () => void;
  previousStep: () => void;
}

interface SectionItem {
  id: string;
  title: string;
  description: string;
  enabled: boolean;
}

const initialSections: SectionItem[] = [
  {
    id: 'compliance-overview',
    title: 'Compliance Overview',
    description: 'Compliant / partial / non-compliant breakdown and active cycle status',
    enabled: true,
  },
  {
    id: 'controls-section',
    title: 'Controls Section',
    description: 'Active controls, overdue tests, first-test needed, and effectiveness stats',
    enabled: true,
  },
  {
    id: 'pending-approvals',
    title: 'Pending Approvals',
    description: 'Items waiting for approval across compliance and controls',
    enabled: true,
  },
  {
    id: 'overdue-due-soon',
    title: 'Overdue & Due Soon',
    description: 'Items past deadline or coming due within 7 days',
    enabled: true,
  },
  {
    id: 'recent-activity-feed',
    title: 'Recent Activity Feed',
    description: 'Latest workflow actions across all modules',
    enabled: true,
  },
];

const ExecutiveDashboardConfig = ({ nextStep, previousStep }: ExecutiveDashboardConfigProps) => {
  const [sections, setSections] = useState<SectionItem[]>(initialSections);

  const toggleSection = (id: string) => {
    setSections((prev) => prev.map((section) => (section.id === id ? { ...section, enabled: !section.enabled } : section)));
  };

  return (
    <div className="exec-dashboard-wrapper">
      {/* Header */}
      <div className="exec-dashboard-inner">
        <h2 className="exec-dashboard-title">Executive Dashboard Config</h2>
        <p className="exec-dashboard-subtitle">Configure Executive Dashboard sections</p>
      </div>

      {/* Info Banner */}
      <div className="exec-dashboard-info-banner">
        <Info size={16} className="exec-dashboard-info-icon" />
        <span className="exec-dashboard-info-text">Configure which sections appear on the Executive Dashboard.</span>
      </div>

      {/* Section List */}
      <div className="exec-dashboard-list">
        {sections.map((section) => (
          <div className="exec-dashboard-row" key={section.id}>
            <div className="exec-dashboard-row-text">
              <h3 className="exec-dashboard-row-title">{section.title}</h3>
              <p className="exec-dashboard-row-description">{section.description}</p>
            </div>

            <button
              type="button"
              role="switch"
              aria-checked={section.enabled}
              onClick={() => toggleSection(section.id)}
              className={`exec-dashboard-toggle ${section.enabled ? 'exec-dashboard-toggle-on' : ''}`}
            >
              <span className="exec-dashboard-toggle-knob" />
            </button>
          </div>
        ))}
      </div>

      {/* Buttons */}
      <div className="organization-footer">
        <button onClick={previousStep} className="organization-back-btn">
          <ArrowLeft size={18} />
          Back
        </button>

        <button onClick={nextStep} className="organization-next-btn">
          Next
          <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );
};

export default ExecutiveDashboardConfig;
